import { useState } from 'react';
import { Link } from 'react-router-dom';
import SATDomainCard from './components/SATDomainCard';
import { satSections, satSectionMap } from './data/satCurriculumData';
import SEOHead from '../../components/SEOHead';

export default function SATPrep() {
  const [selectedSection, setSelectedSection] = useState<'math' | 'english'>('math');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: 'SAT Test Preparation — Math & Reading Writing | Augustine Anyimadu-Ahenkae',
      description:
        'Comprehensive SAT test prep covering all math domains (Algebra, Advanced Math, Problem-Solving, Geometry/Trig) and Reading & Writing sections. Full mock exams, practice tests, and personalized study plans.',
      url: `${siteUrl}/sat-prep`,
      provider: {
        '@type': 'Person',
        name: 'Augustine Anyimadu-Ahenkae',
        url: siteUrl,
      },
      educationalLevel: 'High School',
      inLanguage: 'en',
      hasCourseInstance: [
        { '@type': 'CourseInstance', name: 'SAT Algebra Practice', courseMode: 'online', url: `${siteUrl}/sat-prep/algebra-practice` },
        { '@type': 'CourseInstance', name: 'SAT Advanced Math Practice', courseMode: 'online', url: `${siteUrl}/sat-prep/advanced-math-practice` },
        { '@type': 'CourseInstance', name: 'SAT Problem-Solving & Data Analysis', courseMode: 'online', url: `${siteUrl}/sat-prep/problem-solving-practice` },
        { '@type': 'CourseInstance', name: 'SAT Geometry & Trigonometry', courseMode: 'online', url: `${siteUrl}/sat-prep/geometry-trig-practice` },
        { '@type': 'CourseInstance', name: 'SAT Full Mock Exam', courseMode: 'online', url: `${siteUrl}/sat-prep/mock-exam` },
        { '@type': 'CourseInstance', name: 'SAT Reading & Writing Mock Exam', courseMode: 'online', url: `${siteUrl}/sat-prep/mock-exam-rw` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What SAT sections does this prep course cover?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'This SAT prep covers all four Math domains (Algebra, Advanced Math, Problem-Solving & Data Analysis, Geometry & Trig) and all four Reading & Writing domains (Information & Ideas, Craft & Structure, Expression of Ideas, Standard English Conventions).',
          },
        },
        {
          '@type': 'Question',
          name: 'How many practice questions are included?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The SAT prep includes over 400 practice questions across subject-specific tests plus full-length mock exams: 3 math mock exams (44 questions each) and 1 complete Reading & Writing mock exam (54 questions).',
          },
        },
        {
          '@type': 'Question',
          name: 'Is there a full SAT mock exam available?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes! A full SAT mock exam combining both Math (44 questions, 70 minutes) and Reading & Writing (54 questions, 64 minutes) is available, totaling 98 questions in 134 minutes — matching the real SAT format.',
          },
        },
      ],
    },
  ];

  const practiceTests = [
    {
      id: 'algebra',
      title: 'Algebra Practice Test',
      description: 'Linear equations, functions, inequalities, and systems',
      questionCount: 20,
      estimatedTime: '25 min',
      difficulty: 'Medium',
      link: '/sat-prep/algebra-practice',
      color: 'from-emerald-500 to-teal-500'
    },
    {
      id: 'advanced-math',
      title: 'Advanced Math Practice Test',
      description: 'Quadratics, polynomials, exponentials, and rational expressions',
      questionCount: 20,
      estimatedTime: '30 min',
      difficulty: 'Hard',
      link: '/sat-prep/advanced-math-practice',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      id: 'problem-solving',
      title: 'Problem-Solving & Data Analysis',
      description: 'Ratios, rates, percentages, statistics, probability, and data interpretation',
      questionCount: 20,
      estimatedTime: '25 min',
      difficulty: 'Medium',
      link: '/sat-prep/problem-solving-practice',
      color: 'from-cyan-500 to-sky-500'
    },
    {
      id: 'geometry-trig',
      title: 'Geometry & Trigonometry Practice Test',
      description: 'Area, volume, coordinate geometry, triangles, and SOH-CAH-TOA',
      questionCount: 20,
      estimatedTime: '28 min',
      difficulty: 'Medium',
      link: '/sat-prep/geometry-trig-practice',
      color: 'from-amber-500 to-orange-500'
    }
  ];

  const rwPracticeTests = [
    {
      id: 'information-ideas',
      title: 'Information & Ideas',
      description: 'Central ideas, command of evidence, inferences, and dual-text analysis',
      questionCount: 30,
      estimatedTime: '35 min',
      difficulty: 'Medium',
      link: '/sat-prep/rw-information-ideas',
      color: 'from-rose-500 to-pink-500'
    },
    {
      id: 'craft-structure',
      title: 'Craft & Structure',
      description: 'Words in context, text structure, author\'s purpose, and cross-text connections',
      questionCount: 30,
      estimatedTime: '35 min',
      difficulty: 'Hard',
      link: '/sat-prep/rw-craft-structure',
      color: 'from-pink-500 to-fuchsia-500'
    },
    {
      id: 'expression-of-ideas',
      title: 'Expression of Ideas',
      description: 'Transitions, rhetorical synthesis, and effective sentence combination',
      questionCount: 30,
      estimatedTime: '30 min',
      difficulty: 'Medium',
      link: '/sat-prep/rw-expression-ideas',
      color: 'from-fuchsia-500 to-violet-500'
    },
    {
      id: 'standard-english',
      title: 'Standard English Conventions',
      description: 'Grammar, punctuation, subject-verb agreement, pronoun usage, and verb forms',
      questionCount: 30,
      estimatedTime: '30 min',
      difficulty: 'Medium',
      link: '/sat-prep/rw-standard-english',
      color: 'from-violet-500 to-indigo-500'
    }
  ];

  const currentSection = satSections.find((s) => s.id === selectedSection);

  const renderPracticeTests = () => {
    if (selectedSection === 'math') {
      return (
        <>
          {/* Full Mock Exam Banners — 3 exams */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
              <i className="ri-timer-flash-line text-emerald-600 w-5 h-5 flex items-center justify-center" />
              Full-Length Timed Mock Exams
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                {
                  num: 1,
                  title: 'Mock Exam 1',
                  subtitle: 'Original question set',
                  link: '/sat-prep/mock-exam',
                  color: 'from-emerald-500 to-teal-500',
                  badge: 'bg-emerald-100 text-emerald-700',
                  btn: 'bg-emerald-600 hover:bg-emerald-700',
                },
                {
                  num: 2,
                  title: 'Mock Exam 2',
                  subtitle: 'All-new question set',
                  link: '/sat-prep/mock-exam-2',
                  color: 'from-violet-500 to-rose-500',
                  badge: 'bg-violet-100 text-violet-700',
                  btn: 'bg-violet-600 hover:bg-violet-700',
                },
                {
                  num: 3,
                  title: 'Mock Exam 3',
                  subtitle: 'All-new question set',
                  link: '/sat-prep/mock-exam-3',
                  color: 'from-indigo-500 to-fuchsia-500',
                  badge: 'bg-indigo-100 text-indigo-700',
                  btn: 'bg-indigo-600 hover:bg-indigo-700',
                },
              ].map((exam) => (
                <Link
                  key={exam.num}
                  to={exam.link}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
                >
                  <div className={`h-2 bg-gradient-to-r ${exam.color}`} />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${exam.color} flex items-center justify-center text-white font-black text-xl`}>
                        {exam.num}
                      </div>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${exam.badge}`}>TIMED</span>
                    </div>
                    <h4 className="text-lg font-black text-gray-900 mb-1">{exam.title}</h4>
                    <p className="text-xs text-gray-500 mb-4">{exam.subtitle}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mb-5">
                      <span className="flex items-center gap-1"><i className="ri-question-line" /> 44 Qs</span>
                      <span className="flex items-center gap-1"><i className="ri-time-line" /> 70 min</span>
                      <span className="flex items-center gap-1"><i className="ri-layout-grid-line" /> 4 Domains</span>
                    </div>
                    <div className={`w-full py-2.5 ${exam.btn} text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors whitespace-nowrap`}>
                      Start Exam
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Study Plan Banner */}
          <div className="mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  📊 Get Your Personalized Study Plan
                </h3>
                <p className="text-gray-600">
                  Based on your mock exam results, we'll recommend which domains to focus on
                </p>
              </div>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/sat-prep/study-plan')}
                className="whitespace-nowrap bg-emerald-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors"
              >
                View Study Plan
              </button>
            </div>
          </div>

          {/* Math Practice Tests */}
          <div className="grid md:grid-cols-2 gap-6">
            {satSectionMap['math'].domains.map((domain) => (
              <SATDomainCard key={domain.id} domain={domain} />
            ))}
          </div>
        </>
      );
    }

    // Reading & Writing Section
    return (
      <>
        {/* Full R&W Mock Exam Banner */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <i className="ri-timer-flash-line text-violet-600 w-5 h-5 flex items-center justify-center" />
            Full-Length Timed Mock Exam
          </h3>
          <div className="grid md:grid-cols-1 gap-4">
            <Link
              to="/sat-prep/mock-exam-rw"
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
            >
              <div className="h-2 bg-gradient-to-r from-violet-600 to-rose-500" />
              <div className="p-6 flex flex-col md:flex-row items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600 to-rose-500 flex items-center justify-center text-white flex-shrink-0">
                  <i className="ri-book-open-line text-3xl" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h4 className="text-xl font-black text-gray-900">SAT Reading &amp; Writing Full Mock Exam</h4>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-violet-100 text-violet-700">TIMED</span>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-rose-100 text-rose-700">NEW</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">All 4 domains · Step-by-step explanations · Full results breakdown</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><i className="ri-question-line" /> 54 Questions</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line" /> 64 Minutes</span>
                    <span className="flex items-center gap-1"><i className="ri-layout-grid-line" /> 4 Domains</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="px-7 py-3 bg-gradient-to-r from-violet-600 to-rose-500 text-white font-bold rounded-xl text-sm flex items-center gap-2 whitespace-nowrap group-hover:from-violet-700 group-hover:to-rose-600 transition-all">
                    Start Exam
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>

        {/* R&W Practice Tests */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Practice Tests</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Master every domain of SAT Reading &amp; Writing with 30 comprehensive questions per test and full step-by-step explanations
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {rwPracticeTests.map((test) => (
              <Link
                key={test.id}
                to={test.link}
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${test.color}`}></div>
                <div className="p-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${test.color} flex items-center justify-center text-white text-2xl`}>
                      <i className="ri-file-list-3-line"></i>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      test.difficulty === 'Hard'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {test.difficulty}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-rose-600 transition-colors">
                    {test.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                    {test.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center">
                        <i className="ri-question-line mr-1.5 w-4 h-4 flex items-center justify-center"></i>
                        <span className="font-medium">{test.questionCount} Qs</span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-time-line mr-1.5 w-4 h-4 flex items-center justify-center"></i>
                        <span className="font-medium">{test.estimatedTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center text-rose-600 font-semibold group-hover:translate-x-1 transition-transform">
                      Start Test
                      <i className="ri-arrow-right-line ml-2"></i>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* R&W Curriculum Domains */}
        <div className="grid gap-6">
          {satSectionMap['english'].domains.map((domain) => (
            <SATDomainCard key={domain.id} domain={domain} />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEOHead
        title="SAT Test Prep New York | Math & Reading Writing | Augustine Anyimadu-Ahenkae"
        description="Master the SAT with comprehensive prep covering all Math and Reading & Writing domains. Full mock exams, 400+ practice questions, personalized study plans, and step-by-step solutions by NYC's award-winning math teacher."
        canonical="/sat-prep"
        keywords="SAT test prep, SAT math practice, SAT reading writing, SAT mock exam, SAT study plan, New York SAT tutoring, digital SAT preparation"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-2xl mb-6">
              <i className="ri-book-open-line text-4xl"></i>
            </div>
            <h1 className="text-5xl font-bold mb-4">SAT Test Prep</h1>
            <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
              Master every section of the SAT with comprehensive curriculum coverage and practice tests
            </p>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Full Mock Exam Banner */}
        <div className="mb-12">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-sky-500 to-indigo-700 p-8 shadow-2xl">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30"></div>
            
            <div className="relative z-10">
              <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                    <i className="ri-trophy-line text-xl text-yellow-300"></i>
                    <span className="text-sm font-semibold text-white">Complete SAT Experience</span>
                  </div>
                  
                  <h2 className="text-3xl font-bold text-white mb-3">
                    Full SAT Mock Exam
                  </h2>
                  
                  <p className="text-lg text-indigo-50 mb-6 leading-relaxed">
                    Take the complete SAT test with both Math and Reading & Writing sections in one sitting. Experience the real exam format with accurate timing and comprehensive scoring.
                  </p>
                  
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <i className="ri-file-list-3-line text-xl text-white"></i>
                      <span className="text-white font-medium">98 Questions</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <i className="ri-timer-line text-xl text-white"></i>
                      <span className="text-white font-medium">134 Minutes</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <i className="ri-book-open-line text-xl text-white"></i>
                      <span className="text-white font-medium">Math + R&W</span>
                    </div>
                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                      <i className="ri-focus-3-line text-xl text-white"></i>
                      <span className="text-white font-medium">8 Domains</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/sat-prep/full-mock-exam')}
                    className="inline-flex items-center gap-3 bg-white text-indigo-700 px-8 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-circle-fill text-2xl"></i>
                    Start Full Exam
                  </button>
                </div>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 min-w-[240px]">
                  <h3 className="text-white font-semibold mb-4 text-lg">Exam Breakdown</h3>
                  <div className="space-y-3">
                    <div className="border-b border-white/20 pb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-indigo-100 font-medium">Reading & Writing</span>
                        <span className="text-white font-bold">54 Qs</span>
                      </div>
                      <div className="text-sm text-indigo-200">64 minutes · 4 domains</div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-indigo-100 font-medium">Math</span>
                        <span className="text-white font-bold">44 Qs</span>
                      </div>
                      <div className="text-sm text-indigo-200">70 minutes · 4 domains</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white rounded-full p-1.5 shadow-lg">
            <button
              onClick={() => setSelectedSection('math')}
              className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap ${
                selectedSection === 'math'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-calculator-line mr-2"></i>
              SAT Math
            </button>
            <button
              onClick={() => setSelectedSection('english')}
              className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap ${
                selectedSection === 'english'
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-book-read-line mr-2"></i>
              Reading &amp; Writing
            </button>
          </div>
        </div>

        {/* Test Overview */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {selectedSection === 'math' ? 'SAT Math Section' : 'SAT Reading & Writing Section'}
              </h2>
              <div className="space-y-3">
                <div className="flex items-center text-gray-700">
                  <i className="ri-question-line text-emerald-600 mr-3 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="font-medium">
                    {selectedSection === 'math' ? '44 questions' : '54 questions'}
                  </span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-time-line text-emerald-600 mr-3 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="font-medium">
                    {selectedSection === 'math' ? '70 minutes' : '64 minutes'}
                  </span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-calculator-line text-emerald-600 mr-3 text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span className="font-medium">
                    {selectedSection === 'math'
                      ? 'Calculator allowed for most questions'
                      : 'Digital adaptive testing format'}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-3">Test Structure</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                {selectedSection === 'math'
                  ? 'The SAT Math section tests your ability to solve problems using mathematical concepts, analyze data, and apply mathematical reasoning. Questions range from basic arithmetic to advanced algebra and geometry.'
                  : 'The SAT Reading & Writing section assesses your ability to comprehend complex texts, analyze arguments, and demonstrate command of standard English conventions through grammar and vocabulary questions.'}
              </p>
            </div>
          </div>
        </div>

        {/* Practice Tests Section */}
        {selectedSection === 'math' && (
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Practice Tests</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Test your knowledge with full-length practice tests featuring real SAT-style questions and detailed solutions
              </p>
            </div>

            {/* Full Mock Exam Banners — 3 exams */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <i className="ri-timer-flash-line text-emerald-600 w-5 h-5 flex items-center justify-center" />
                Full-Length Timed Mock Exams
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  {
                    num: 1,
                    title: 'Mock Exam 1',
                    subtitle: 'Original question set',
                    link: '/sat-prep/mock-exam',
                    color: 'from-emerald-500 to-teal-500',
                    badge: 'bg-emerald-100 text-emerald-700',
                    btn: 'bg-emerald-600 hover:bg-emerald-700',
                  },
                  {
                    num: 2,
                    title: 'Mock Exam 2',
                    subtitle: 'All-new question set',
                    link: '/sat-prep/mock-exam-2',
                    color: 'from-violet-500 to-rose-500',
                    badge: 'bg-violet-100 text-violet-700',
                    btn: 'bg-violet-600 hover:bg-violet-700',
                  },
                  {
                    num: 3,
                    title: 'Mock Exam 3',
                    subtitle: 'All-new question set',
                    link: '/sat-prep/mock-exam-3',
                    color: 'from-indigo-500 to-fuchsia-500',
                    badge: 'bg-indigo-100 text-indigo-700',
                    btn: 'bg-indigo-600 hover:bg-indigo-700',
                  },
                ].map((exam) => (
                  <Link
                    key={exam.num}
                    to={exam.link}
                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100"
                  >
                    <div className={`h-2 bg-gradient-to-r ${exam.color}`} />
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${exam.color} flex items-center justify-center text-white font-black text-xl`}>
                          {exam.num}
                        </div>
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${exam.badge}`}>TIMED</span>
                      </div>
                      <h4 className="text-lg font-black text-gray-900 mb-1">{exam.title}</h4>
                      <p className="text-xs text-gray-500 mb-4">{exam.subtitle}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mb-5">
                        <span className="flex items-center gap-1"><i className="ri-question-line" /> 44 Qs</span>
                        <span className="flex items-center gap-1"><i className="ri-time-line" /> 70 min</span>
                        <span className="flex items-center gap-1"><i className="ri-layout-grid-line" /> 4 Domains</span>
                      </div>
                      <div className={`w-full py-2.5 ${exam.btn} text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors whitespace-nowrap`}>
                        Start Exam
                        <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Study Plan Banner */}
            <Link
              to="/sat-prep/study-plan"
              className="group block bg-gradient-to-r from-teal-500 to-cyan-500 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden mb-6"
            >
              <div className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-5">
                  <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-road-map-line text-white text-3xl" />
                  </div>
                  <div className="text-white">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-xl font-black">Personalized Study Plan</h3>
                      <span className="bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">AI-POWERED</span>
                    </div>
                    <p className="text-teal-100 text-sm max-w-xl">
                      Get a custom study plan based on your mock exam weak areas — domain priority ranking, weekly schedule, and targeted tips.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-white text-teal-700 font-black px-7 py-3 rounded-xl group-hover:bg-teal-50 transition-colors whitespace-nowrap flex-shrink-0">
                  View Study Plan
                  <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>

            <div className="grid md:grid-cols-2 gap-6">
              {practiceTests.map((test) => (
                <Link
                  key={test.id}
                  to={test.link}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  <div className={`h-2 bg-gradient-to-r ${test.color}`} />
                  <div className="p-8">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${test.color} flex items-center justify-center text-white text-2xl`}>
                        <i className="ri-file-list-3-line" />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        test.difficulty === 'Hard' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {test.difficulty}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">
                      {test.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-6 leading-relaxed">{test.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <i className="ri-question-line mr-1.5 w-4 h-4 flex items-center justify-center" />
                          <span className="font-medium">{test.questionCount} Qs</span>
                        </div>
                        <div className="flex items-center">
                          <i className="ri-time-line mr-1.5 w-4 h-4 flex items-center justify-center" />
                          <span className="font-medium">{test.estimatedTime}</span>
                        </div>
                      </div>
                      <div className="flex items-center text-emerald-600 font-semibold group-hover:translate-x-1 transition-transform">
                        Start Test
                        <i className="ri-arrow-right-line ml-2" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Curriculum Domains */}
        <div className="mb-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Curriculum Overview</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore the complete breakdown of topics, skills, and question types you'll encounter on test day
            </p>
          </div>

          <div className="grid gap-6">
            {(currentSection?.domains ?? []).map((domain) => (
              <SATDomainCard key={domain.id} domain={domain} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
